import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTranslations } from '../context/TranslationsContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Tag, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getLocalizedPath } from '../utils/routes';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTH_KEYS_FULL = ['january','february','march','april','may_full','june','july','august','september','october','november','december'];
const DAY_KEYS = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

const EventsPage = () => {
  const { i18n } = useTranslation();
  const { td } = useTranslations();
  const lang = i18n.language;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/api/events`);
      setEvents(res.data);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const ls = (field, fallback = '') => {
    if (!field) return fallback;
    if (typeof field === 'string') return field;
    return field[lang] || field.pl || field.en || Object.values(field)[0] || fallback;
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getDate();
    const monthKey = MONTH_KEYS_FULL[d.getMonth()];
    const month = td(`months.${monthKey}`) || d.toLocaleDateString('pl-PL', { month: 'long' });
    return `${day} ${month}`;
  };

  const formatDayOfWeek = (dayIndex) => {
    const key = DAY_KEYS[dayIndex];
    return td(`days.${key}`) || key;
  };

  const getWhenLabel = (event) => {
    if (!event.isRecurring) {
      return formatDate(event.nextDate || event.date);
    }
    const days = (event.recurringConfig?.daysOfWeek || [])
      .sort((a, b) => a - b)
      .map(d => formatDayOfWeek(d))
      .join(', ');
    return `${td('events.every') || 'Każdy'} ${days}`;
  };

  const getUntilLabel = (event) => {
    if (!event.isRecurring || !event.recurringConfig?.endDate) return '';
    return `${td('events.until') || 'do'} ${formatDate(event.recurringConfig.endDate)}`;
  };

  const getBadgeLabel = (badge) => {
    if (typeof badge === 'string') return badge;
    return badge[lang] || badge.pl || badge.en || Object.values(badge)[0] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen page-gradient pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-white/10 rounded-xl aspect-square mb-4" />
                <div className="h-6 bg-white/10 rounded w-3/4 mb-2" />
                <div className="h-4 bg-white/10 rounded w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-gradient min-h-screen pt-28 sm:pt-32 pb-20 px-4 sm:px-6 md:px-12" data-testid="events-page">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {td('events.title') || 'Wydarzenia'}
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {td('events.subtitle') || 'Zobacz nasz kalendarz wydarzeń'}
          </p>
        </motion.div>

        {events.length === 0 ? (
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400 text-lg">{td('events.noEvents') || 'Brak nadchodzących wydarzeń'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`${getLocalizedPath('events')}/${event.slug}`} data-testid={`event-${event.slug}`}>
                  <Card className="bg-white/5 border-white/10 overflow-hidden hover:border-primary/50 transition-all duration-300 group h-full">
                    <div className="relative aspect-square overflow-hidden bg-white/5">
                      {event.image ? (
                        <img
                          src={event.image.startsWith('/') ? `${API}${event.image}` : event.image}
                          alt={ls(event.title)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-purple-600/20">
                          <Calendar className="w-16 h-16 text-white/20" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                        {event.badges?.map((badge, i) => (
                          <span key={i} className="rounded-full border border-primary/30 bg-primary/20 px-2.5 py-0.5 text-[11px] text-primary backdrop-blur-sm">
                            {getBadgeLabel(badge)}
                          </span>
                        ))}
                        {event.isRecurring && (
                          <span className="rounded-full border border-white/10 bg-black/40 px-2.5 py-0.5 text-[11px] text-white/75 backdrop-blur-sm">
                            {td('events.recurring') || 'Cykliczne'}
                          </span>
                        )}
                      </div>
                      <div className="absolute bottom-3 left-3 right-3">
                        <span className="rounded-full border border-white/10 bg-black/40 px-3 py-1 text-xs text-white/80 backdrop-blur-sm">
                          {getWhenLabel(event)} {event.time && `• ${event.time}`}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <h3 className="text-lg font-semibold text-white mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                        {ls(event.title)}
                      </h3>
                      {event.isRecurring && (
                        <p className="text-sm text-gray-500">{getUntilLabel(event)}</p>
                      )}
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-primary font-semibold">
                          {event.price === 0 || event.price == null
                            ? (td('events.free') || 'Bezpłatne')
                            : `${event.price.toFixed(2)} zł`}
                        </span>
                        <span className="text-sm text-gray-400 group-hover:text-primary transition-colors flex items-center gap-1">
                          {td('events.details') || 'Szczegóły'} <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
